import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Sparkles, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cleanMd = (t) => t ? t.replace(/\*\*/g, '').replace(/\*/g, '').replace(/#{1,3}\s/g, '') : t;

export default function AIPageInsight({ token, period, refDate, context, pageTitle }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasLoaded = useRef(false);

  const loadInsight = () => {
    if (hasLoaded.current || loading || !token || !context) return;
    hasLoaded.current = true;
    setExpanded(true);
    setLoading(true);

    const prompt = `Generate a 3-4 sentence actionable insight for "${pageTitle}". Structure: (1) What's happening with specific numbers, (2) The consequence if this trend continues, (3) One specific action with expected outcome. Use Cr/L. Be decisive.\n\nData:\n${context}`;

    axios.post(`${API}/ai/copilot`, {
      message: prompt,
      session_id: `page-${pageTitle.toLowerCase().replace(/\s+/g, '-')}-${period}-${refDate}`,
      period,
      ref_date: refDate,
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setInsight(cleanMd(r.data.response)))
      .catch(() => setInsight("Unable to generate insight at this time."))
      .finally(() => setLoading(false));
  };

  // Reset when context changes
  useEffect(() => {
    hasLoaded.current = false;
    setInsight(null);
    setExpanded(false);
  }, [period, refDate]);

  return (
    <div className="rounded-xl bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-100" data-testid="ai-page-insight">
      <button
        onClick={loadInsight}
        className="w-full flex items-center justify-between px-3 sm:px-4 py-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-3 w-3 text-blue-600" />
          </div>
          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
            {insight ? 'AI Insight' : 'Tap to generate AI Insight'}
          </span>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-blue-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-3 sm:px-4 pb-3">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-blue-500">
              <div className="h-3 w-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              Analyzing...
            </div>
          ) : insight ? (
            <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">{insight}</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
